import {
    getFriendsScores,
    getServerTimestamp,
    getUserData,
    getUserDataByKeys,
    LeaderboardEntry,
    loginWithGameBackendCredentials,
    PlainObject,
    postScore,
    registerWithFacebookInstantGameSignature as gbcRegisterWithFacebookInstantGameSignature,
    setUserProfile,
    syncFriendListByPlatformPlayerId as gbcSyncFriendListByPlatformPlayerId,
    trackBotMessageConversion as gbcTrackBotMessageConversion,
    close as gbcClose,
    isConnected as isGBCConnected,
    setUserData,
    removeUserDataByKeys,
    closeEvent,
} from '@softgames/game-backend-client';
import { AsyncActionStatus } from 'State/AsyncAction';
import { DataConflictActions } from 'State/Game/DataConflictActions';
import { GameActions } from 'State/Game/GameActions';
import { IGameState } from 'State/Game/IGameState';
import DateHelper from 'Utility/DateHelper';
import { Logger } from 'Utility/Logger/Logger';
import { LogOrigin } from 'Utility/Logger/LogOrigin';
import { LogStyles } from 'Utility/Logger/LogStyles';
import { ConnectionEventName } from 'Utility/Tracking/TrackingEvent';

import Game from '../../Game';
import IPlayerData from '../../Player/IPlayerData';
import { Categorizer, convertError, report } from '../../report';
import { Retrier, retry } from '../async';
import { Deferred } from '../Deferred';
import { resetEventHandlerData, STRING_DATA_NOT_FOUND, track } from '../Tracking/Track';
import { fbPendingRequestCategorizer } from './Drivers/FBHelpers';
import { GBError, GBErrorMap } from './GBError';

export namespace GBCHandler {
    // Login

    export interface AuthData {
        userId: string;
        authToken: string;
    }

    let loggedIn = new Deferred<boolean>(false);
    let lastConnectedAt: Date;
    let sessionNum: number = 0;
    let failedBeforeSuccess = false;

    let loginStartedAt: Date;

    export function login(params: {
        gameId: string;
        cacheRead: () => Promise<AuthData | undefined>;
        cacheWrite: (data: AuthData | null) => Promise<void>;
        register: () => Promise<AuthData>;
    }) {
        if (loggedIn.read.isSettled()) {
            resetGBCLoggedInStatus();
        }
        const { gameId, cacheRead, cacheWrite, register } = params;
        loginStartedAt = DateHelper.now;
        return retry<AuthData>(async () => {
            let caching = Promise.resolve();
            let authData: AuthData | null = null;
            try {
                authData = await getAuthDataFromCache();
                if (!authData) {
                    authData = await register();
                    if (!isValidAuthData(authData)) {
                        throw new Error('Failed to register');
                    }
                    caching = cacheAuthData(authData).catch(cachingError => {
                        // log to sentry but don't propagate up as this is a nice-to-have
                        report('cacheAuthData failed', {
                            once: true,
                            sampleRate: 0.1,
                            error: cachingError,
                            categorizers: [fbPendingRequestCategorizer('cacheAuthData')],
                        });
                    });
                }
                await loginWithGameBackendCredentials({
                    userId: authData.userId,
                    authToken: authData.authToken,
                    gameId,
                    prioritiseLogin: true,
                });
            } catch (error) {
                try {
                    await caching;
                    await invalidateAuthDataCache();
                } catch (invalidationError) {
                    // log to sentry, but don't stop retry logic
                    report('invalidateAuthDataCache failed', {
                        once: true,
                        sampleRate: 0.1,
                        error: invalidationError,
                        categorizers: [fbPendingRequestCategorizer('invalidateAuthDataCache')],
                    });
                }
                throw convertError(error, 'login flow');
            }
            // TODO: await caching?
            return authData;
        }).promise.then(
            authData => {
                loggedIn.control.resolve(true);
                Logger.log(`Authenticated with Game Backend`, LogOrigin.GBC);
                return authData;
            },
            error => {
                if (sessionNum === 0) {
                    failedBeforeSuccess = true;
                }
                loggedIn.control.reject(error);
                report('Failed login retries!', {
                    error,
                    once: true,
                    sampleRate: 0.1,
                    fingerprint: 'GBCHandler:login:FailedLoginRetries',
                });
                throw error;
            }
        );

        function isValidAuthData(potentialAuthData: any): potentialAuthData is AuthData {
            return potentialAuthData && potentialAuthData.userId && potentialAuthData.authToken;
        }

        async function getAuthDataFromCache() {
            const authData = await cacheRead();
            return isValidAuthData(authData) ? authData : undefined;
        }
        function cacheAuthData(data: AuthData) {
            return cacheWrite(data);
        }
        function invalidateAuthDataCache() {
            return cacheWrite(null);
        }
    }

    export async function onLogin(params: {
        syncFriendsList: () => Promise<void>;
        userProfile: Deferred<IGBUserProfile>;
        botMessageId: Deferred<string>;
    }): Promise<void> {
        trackOnLogin();
        lastConnectedAt = DateHelper.now;
        sessionNum++;
        params.syncFriendsList();
        params.userProfile.read.nowOrThen(userProfile => {
            GBCHandler.updateUserProfile(userProfile);
            track('userID', Game.sdk.getUserId() || STRING_DATA_NOT_FOUND);
        });

        params.botMessageId.read.nowOrThen(messageId => {
            GBCHandler.trackBotMessageConversion({ messageId });
        });
    }

    export function registerWithFacebookInstantGameSignature(
        ...args: Parameters<typeof gbcRegisterWithFacebookInstantGameSignature>
    ) {
        return gbcRegisterWithFacebookInstantGameSignature(...args);
    }

    //

    export function syncFriendListByPlatformPlayerId(...args: Parameters<typeof gbcSyncFriendListByPlatformPlayerId>) {
        return retry(
            async () => {
                await gbcSyncFriendListByPlatformPlayerId(...args);
            },
            {
                getRetryDelayMs: () => 1000,
                onError: (error, attemptIndex) => {
                    if (attemptIndex === 5) {
                        report('syncFriendsList failed 5 times in row', {
                            error,
                            once: true,
                            categorizers: [gbcNetworkErrorCategorizer('syncFriendsList')],
                        });
                    }
                },
            }
        ).promise;
    }

    export async function trackBotMessageConversion(...args: Parameters<typeof gbcTrackBotMessageConversion>) {
        try {
            await retry(() => gbcTrackBotMessageConversion(...args)).promise;
        } catch (error) {
            report('gbcTrackBotMessageConversion failed', {
                once: true,
                sampleRate: 0.1,
                error,
            });
        }
    }

    export async function debugLoadFromGBC() {
        return await getUserData();
    }

    export async function loadFromGBC(keys: string[]): Promise<IPlayerData> {
        const canProceed = await loggedIn.read.promise.catch(() => false);
        if (!canProceed) return;
        return ((await getUserDataByKeys({ keys })) as unknown) as IPlayerData;
    }

    export async function saveToGBC(data: Partial<IPlayerData>): Promise<void> {
        const canProceed = await loggedIn.read.promise.catch(() => false);
        if (!canProceed) return;

        const keysToRemove = getKeysWithNullOfUndefinedValues();
        if (keysToRemove.length > 0) {
            await removeUserDataByKeys(keysToRemove);
        }
        Logger.log('Saving to GBC', LogOrigin.GBC);
        const plainObject = toPlainObject(data as any);
        return await setUserData(plainObject);

        function getKeysWithNullOfUndefinedValues() {
            return Object.keys(data).filter((key: keyof IPlayerData) => data[key] === null || data[key] === undefined);
        }
    }

    export async function removeDataByKey(keys: string[]) {
        const canProceed = await loggedIn.read.promise.catch(() => false);
        if (!canProceed) {
            report("Can't clear gbc keys if not connected", {
                once: true,
                sampleRate: 0.1,
                fingerprint: 'FBSDKDriver:removeDataByKey:NotConnected',
            });
            return;
        }
        return await removeUserDataByKeys(keys);
    }

    /**
     * Does not support circular structures
     * Does not look closely at prototypes and functions
     * Does not compare advances data types like Map and Set
     */
    function simpleEqual(a: any, b: any) {
        if (a === b) return true;
        if (typeof a !== typeof b) return false;
        if (typeof a === 'number' && isNaN(a) && isNaN(b)) return true;
        if (typeof a !== 'object' || a === null || b === null) return false;
        if (Array.isArray(a) !== Array.isArray(b)) return false;

        const compared = Object.create(null);
        for (const key in a) {
            if (!Object.prototype.hasOwnProperty.call(a, key)) continue;
            if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
            if (!simpleEqual(a[key], b[key])) return false;
            compared[key] = true;
        }
        for (const key in b) {
            if (compared[key]) continue;
            if (!Object.prototype.hasOwnProperty.call(b, key)) continue;
            if (!Object.prototype.hasOwnProperty.call(a, key)) return false;
            if (!simpleEqual(b[key], a[key])) return false;
        }

        return true;
    }

    function toPlainObject(data: { [key: string]: string | number | null | undefined | boolean }): PlainObject {
        const plainObject: PlainObject = {};

        Object.entries(data).forEach(([key, value]) => {
            if (value === undefined) {
                report('Attempting to plain object with entry value undefined', {
                    sampleRate: 0.1,
                    once: true,
                    tags: { key },
                });
                return;
            }
            if (value === null) {
                plainObject[key] = value;
            } else if (typeof value === 'boolean' || typeof value === 'number' || typeof value === 'string') {
                plainObject[key] = String(value);
            } else {
                report('Unexpected value type', {
                    sampleRate: 0.1,
                    once: true,
                    tags: {
                        key,
                        type: typeof value,
                    },
                });
            }
        });

        return plainObject;
    }

    //

    export async function updateLeaderboard(leaderboardName: string, instanceId: string, score: number): Promise<void> {
        const canProceed = await loggedIn.read.promise.catch(() => false);
        if (!canProceed) return;

        try {
            await retry(
                () =>
                    postScore({
                        leaderboardName,
                        instanceId,
                        score: score || 0,
                    }),
                {
                    onError: (error, count) => {
                        console.warn(
                            `postScore failed #${count}`,
                            `leaderboardName = "${leaderboardName}"`,
                            `instanceId = "${instanceId}"`,
                            `score = ${score}`,
                            error
                        );
                    },
                }
            ).promise;
        } catch (error) {
            report('postScore failed', {
                once: true,
                sampleRate: 0.01,
                error,
                tags: {
                    leaderboardName,
                    instanceId,
                    score: score || 0,
                },
                categorizers: [gbcNetworkErrorCategorizer('postScore')],
            });
        }
    }

    export type IGBUserProfile = Parameters<typeof setUserProfile>[0];

    export async function updateUserProfile(userProfile: IGBUserProfile) {
        try {
            await retry(() => setUserProfile(userProfile)).promise;
        } catch (error) {
            report('setUserProfile failed', {
                once: true,
                sampleRate: 0.1,
                error,
                categorizers: [gbcNetworkErrorCategorizer('setUserProfile')],
            });
        }
    }

    export async function fetchLeaderboard(leaderboardName: string, instanceId: string): Promise<LeaderboardEntry[]> {
        const canProceed = await loggedIn.read.promise.catch(() => false);
        if (!canProceed) return [];
        try {
            return await retry(
                () =>
                    getFriendsScores({
                        instanceId: instanceId,
                        leaderboardName: leaderboardName,
                    }),
                {
                    getRetryDelayMs: index => [100][index],
                    onError: (error, count) => {
                        console.warn(
                            `getFriendsScores failed #${count}`,
                            `leaderboardName = "${leaderboardName}"`,
                            `instanceId = "${instanceId}"`,
                            error
                        );
                    },
                }
            ).promise;
        } catch (error) {
            report('getFriendsScores failed', {
                once: true,
                sampleRate: 0.1,
                error,
                tags: {
                    leaderboardName,
                    instanceId,
                },
                categorizers: [gbcNetworkErrorCategorizer('getFriendsScores')],
            });
            return [];
        }
    }

    export function getGBCTimestamp(): Promise<number> {
        const retrier = new Retrier(undefined, (error, attemptIndex) => {
            if (attemptIndex === 2) {
                report('Failed to get timestamp 3 times in row', {
                    error,
                    once: true,
                    categorizers: [gbcNetworkErrorCategorizer('getServerTimestamp')],
                });
            }
        });
        return retrier.restart(getServerTimestamp, false) as Promise<number>;
    }

    export function logout() {
        if (!loggedIn.read.isSucceed()) {
            report('Attempting to logout of GBC when not logged in.', {
                once: true,
                sampleRate: 0.1,
                tags: {
                    gbcStatus: Game.store.getState().gbcStatus,
                },
                fingerprint: 'GBCHandler:logout:AlreadyLoggedOut',
            });

            return new Promise<void>((res, rej) => {
                return rej();
            });
        }

        Logger.log('Beginning logout from Game Backend', LogOrigin.GBC);
        return gbcClose();
    }

    export function onLogout(gbcError?: GBError) {
        Logger.log('Logged out from Game Backend', LogOrigin.GBC);
        onDisconnect(gbcError);
    }

    export function onDisconnect(gbcError?: GBError) {
        resetGBCLoggedInStatus();
        trackDisconnect(gbcError);
        lastConnectedAt = DateHelper.now;
        GameActions.disconnectedFromGBC();
    }

    export function onInactivityTimeout(): Promise<void> {
        return logout()
            .then(() => onLogout(GBError.GAME_TIMEOUT))
            .catch(error => {
                report('Failed to logout from GBC', {
                    error,
                    once: true,
                    sampleRate: 0.1,
                    fingerprint: 'GBCHandler:OnInactivityTimeout:FailedToLogout',
                });
            });
    }

    function resetGBCLoggedInStatus(): void {
        loggedIn = new Deferred<boolean>(false);
    }
    function trackOnLogin() {
        try {
            const gbInitializeTime = DateHelper.now.getTime() - loginStartedAt.getTime();
            track('gbInitializeTime', gbInitializeTime);
            track('gbFailBeforeSuccess', Number(GBCHandler.getFailedBeforeSuccess()));
            if (sessionNum > 0) {
                trackReconnect();
            }
        } catch (error) {
            report('Failed to track gbc login', {
                error,
                sampleRate: 0.1,
                once: true,
                fingerprint: 'TrackGBCOnLogin:FailedToTrack',
            });
        }
    }
    function trackReconnect(): void {
        try {
            const disconnectTime = getTimeDifferenceInRoundedMinutes(lastConnectedAt, DateHelper.now);
            track('gbSessionNumber', sessionNum);
            track('gbDisconnectTime', disconnectTime);

            resetEventHandlerData(ConnectionEventName.gbReconnect, Game.store.getState());
        } catch (error) {
            report('Failed to track gbReconnect', {
                error,
                sampleRate: 0.1,
                once: true,
                fingerprint: 'TrackGBReconnect:FailedToTrack',
            });
        }
    }

    function trackDisconnect(gbcError: GBError = GBError.OTHER): void {
        try {
            track('gbSessionNumber', sessionNum);
            track('gbError', gbcError);
            resetEventHandlerData(ConnectionEventName.gbFail, Game.store.getState());
        } catch (error) {
            report('Failed to track gbReconnect', {
                error,
                sampleRate: 0.1,
                once: true,
                fingerprint: 'TrackGBReconnect:FailedToTrack',
            });
        }
    }

    export function isConnected(): Promise<boolean> {
        return isGBCConnected();
    }

    export function getFailedBeforeSuccess() {
        return failedBeforeSuccess;
    }

    export function parseGBCError(error: any): GBError {
        const errorId: keyof typeof GBErrorMap = error?.parentError?.id;
        const mappedError = GBErrorMap[errorId] ?? GBError.OTHER;
        return mappedError;
    }

    export async function waitForGBCConnectionResolution(state: IGameState) {
        if (state.gbcStatus !== AsyncActionStatus.COMPLETE) {
            // If gbc is not connected wait for connection
            await Game.store.waitFor(GameActions.connectedToGBC);
            state = Game.store.getState();
            // If conflicts found wait for resolution
            if (state.dataConflict.status !== AsyncActionStatus.COMPLETE) {
                await Game.store.waitFor(DataConflictActions.resolveConflicts);
            }
        }
    }

    export function onGBCConnectionClosed(code: number, reason: string) {
        console.log(code, reason);
        Logger.log(reason, LogOrigin.GBC, LogStyles.ERROR);
        onDisconnect();
        /* if (loggedIn.read.isSettled() && loggedIn.read.getValue()) {
            resetGBCLoggedInStatus();
        } */
    }
}

closeEvent.on(GBCHandler.onGBCConnectionClosed);

// Error reporting stuff

function gbcNetworkErrorCategorizer(funcName: string): Categorizer {
    return (error: any) => {
        const notLoggedIn = String(error?.message).includes('flag "loggedIn" did not reach asserted state');
        const timedOut = /\b(timed out)\b/i.test(String(error?.message));
        if (notLoggedIn || timedOut) {
            return {
                fingerprint: 'GBCNetworkError',
                tags: {
                    notLoggedIn,
                    timedOut,
                    funcName,
                },
            };
        }
    };
}

function getTimeDifferenceInRoundedMinutes(a: Date, b: Date) {
    return Math.round((a.getTime() - b.getTime()) / 60000);
}
